<script setup>
import { ref, watch, computed, defineProps, defineEmits, onMounted } from 'vue'
import countries from 'i18n-iso-countries'
import { useI18n } from 'vue-i18n'
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber'
import 'element-plus/dist/index.css'

// Initialize Google phone util
const phoneUtil = PhoneNumberUtil.getInstance()

// Register locales
countries.registerLocale(require('i18n-iso-countries/langs/en.json'))
countries.registerLocale(require('i18n-iso-countries/langs/el.json'))

// Props
const props = defineProps({
  modelValue: String,
  countryValue: String
})

// Emits
const emit = defineEmits([
  'update:modelValue',
  'update:countryValue',
  'update:invalidPhone'
])

const { t, locale } = useI18n()

// Reactive state
const phone = ref('')
const country = ref(props.countryValue || 'GR')
const invalidPhone = ref(false)

// Initialize phone from existing full number (if provided)
onMounted(() => {
  if (props.modelValue) {
    try {
      const parsed = phoneUtil.parse(props.modelValue)
      country.value = phoneUtil.getRegionCodeForNumber(parsed)
      phone.value = phoneUtil.getNationalSignificantNumber(parsed)
    } catch {
      phone.value = ''
    }
  }

  checkPhone()
})

// Countries list based ONLY on Google supported regions
const countriesList = computed(() => {
  const lang = locale.value

  return phoneUtil.getSupportedRegions()
      .map((code) => {
        const name =
            countries.getName(code, lang) ||
            countries.getName(code, 'en') ||
            code

        const callingCode = phoneUtil.getCountryCodeForRegion(code)

        return {
          code,
          label: `+${callingCode} (${name})`,
          value: code
        }
      })
      .sort((a, b) => a.label.localeCompare(b.label))
})


// Validate + emit full international number
const checkPhone = () => {
  if (!phone.value) {
    invalidPhone.value = false
    emit('update:modelValue', '')
    emit('update:invalidPhone', false)
    return
  }

  try {
    const number = phoneUtil.parse(phone.value, country.value)

    if (phoneUtil.isValidNumber(number)) {
      invalidPhone.value = false

      // Full international number (+35799123456)
      const fullNumber = phoneUtil.format(
          number,
          PhoneNumberFormat.E164
      )

      emit('update:modelValue', fullNumber)
    } else {
      invalidPhone.value = true
      emit('update:modelValue', phone.value)
    }
  } catch {
    invalidPhone.value = true
    emit('update:modelValue', phone.value)
  }

  emit('update:invalidPhone', invalidPhone.value)
}


// Watch phone input
watch(phone, () => {
  checkPhone()
})

// Watch country change
watch(country, (val) => {
  phone.value = ''
  emit('update:countryValue', val)
  checkPhone()
})
</script>

<template>
  <div class="w-full max-w-md">
    <div class="flex gap-2 mt-1">

      <!-- Country Code Dropdown -->
      <el-select
          v-model="country"
          filterable
          class="w-1/2"
      >
        <el-option
            v-for="c in countriesList"
            :key="c.code"
            :label="c.label"
            :value="c.code"
        />
      </el-select>

      <!-- Phone Input -->
      <el-input
          v-model="phone"
          :placeholder="t('howToReturnPage.phone')"
          class="flex-1"
      />
    </div>
  </div>
</template>

<style scoped>
:deep(.el-input__wrapper),
:deep(.el-select .el-input__wrapper) {
  border-radius: 0 !important;
}
</style>
